export default `
# Shipments (Outbox service)

The Outbox service manages **drafts** (PDF documents uploaded before sending) and **shipments** (confirmed legal communications to be printed, enveloped, shipped and tracked).

A new shipment is created in three steps:

1. Check product availability and price.
2. Upload the PDF document as a draft.
3. Confirm the shipment using the uploaded draft and the accepted price.

Use the PRE environment for every test: \`https://pre-aws.postamail.es/api\`.

## 1. Check availability and price {#1-check-availability-and-price}

\`\`\`endpoint
GET /api/product/products/price
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/product/products/price\` |
| Operation | \`GET\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`NULL\` |

Before uploading or confirming a shipment, ask Product service whether the requested product and print options are available for the billable/realm and destination. The response is an object of type [ShipmentPriceDetails](/docs/api/shipment-objects#shipmentpricedetails).

### Query parameters

| Name | Type | Required | Description |
|------|------|----------|-------------|
| \`billableidortitle\` | string | yes | Billable identifier or title. In the integration example, \`acme\`. |
| \`productcode\` | string | yes | Product code, such as \`BCC\` or \`BFX\`. |
| \`destinationcountryiso\` | string | yes | Destination country code, always ISO alpha-2, e.g. \`ES\`. |
| \`destinationpostalcode\` | string | yes | Destination postal code. |
| \`printcolor\` | boolean | yes | \`true\` for color, \`false\` for black and white. May be ignored depending on product type. |
| \`printduplex\` | boolean | yes | \`true\` for duplex, \`false\` for simplex. May be ignored depending on product type. |
| \`pages\` | integer | yes | Number of PDF pages to print. |
| \`includevat\` | boolean | yes | \`true\` to include VAT in the calculated total. |

\`\`\`tabs-price
===curl
curl -X GET "https://pre-aws.postamail.es/api/product/products/price?billableidortitle=acme&productcode=BCC&destinationcountryiso=ES&destinationpostalcode=28914&printcolor=false&printduplex=false&pages=5&includevat=true" \\
  -H "Authorization: Bearer <jwtToken>"
===node
const url = 'https://pre-aws.postamail.es/api/product/products/price?' + new URLSearchParams({
  billableidortitle: 'acme',
  productcode: 'BCC',
  destinationcountryiso: 'ES',
  destinationpostalcode: '28914',
  printcolor: 'false',
  printduplex: 'false',
  pages: '5',
  includevat: 'true'
})
const response = await fetch(url, {
  headers: { Authorization: 'Bearer ' + jwtToken }
})
const price = await response.json()
===python
import requests

response = requests.get(
  'https://pre-aws.postamail.es/api/product/products/price',
  headers={'Authorization': 'Bearer ' + jwt_token},
  params={
    'billableidortitle': 'acme',
    'productcode': 'BCC',
    'destinationcountryiso': 'ES',
    'destinationpostalcode': '28914',
    'printcolor': 'false',
    'printduplex': 'false',
    'pages': 5,
    'includevat': 'true'
  }
)
response.raise_for_status()
price = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$query = http_build_query(array(
  'billableidortitle' => 'acme',
  'productcode' => 'BCC',
  'destinationcountryiso' => 'ES',
  'destinationpostalcode' => '28914',
  'printcolor' => 'false',
  'printduplex' => 'false',
  'pages' => 5,
  'includevat' => 'true'
));
$curl = curl_init('https://pre-aws.postamail.es/api/product/products/price?' . $query);
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)
));
$price = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using System.Net.Http.Headers;
using System.Net.Http.Json;

var url = "https://pre-aws.postamail.es/api/product/products/price" +
  "?billableidortitle=acme&productcode=BCC&destinationcountryiso=ES" +
  "&destinationpostalcode=28914&printcolor=false&printduplex=false&pages=5&includevat=true";
using var request = new HttpRequestMessage(HttpMethod.Get, url);
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
var price = await response.Content.ReadFromJsonAsync<ShipmentPriceDetails>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/product/products/price?billableidortitle=acme&productcode=BCC&destinationcountryiso=ES&destinationpostalcode=28914&printcolor=false&printduplex=false&pages=5&includevat=true"))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

The response is an object of type [ShipmentPriceDetails](/docs/api/shipment-objects#shipmentpricedetails). If the product/options are accepted, store the returned values. They must be copied into \`Shipment.features\` when confirming the shipment.

## 2. Upload the PDF {#2-upload-the-pdf}

\`\`\`endpoint
POST /api/outbox/drafts
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/outbox/drafts\` |
| Operation | \`POST\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`multipart/form-data\` with one or more PDF files in field \`files\` |

Upload the document PDF as multipart form data. The response is a collection of [DraftInfo](/docs/api/shipment-objects#draftinfo) objects. Keep the returned \`draftId\`; it is required in \`Shipment.pages\`.

\`\`\`tabs-shipment-upload
===curl
curl -X POST "https://pre-aws.postamail.es/api/outbox/drafts" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -F "files=@./contract.pdf;type=application/pdf"
===node
const form = new FormData()
form.append('files', pdfFile)
const response = await fetch('https://pre-aws.postamail.es/api/outbox/drafts', {
  method: 'POST',
  headers: { Authorization: 'Bearer ' + jwtToken },
  body: form
})
const drafts = await response.json()
===python
with open('contract.pdf', 'rb') as pdf:
  response = requests.post(
    'https://pre-aws.postamail.es/api/outbox/drafts',
    headers={'Authorization': 'Bearer ' + jwt_token},
    files={'files': ('contract.pdf', pdf, 'application/pdf')}
  )
response.raise_for_status()
drafts = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$curl = curl_init('https://pre-aws.postamail.es/api/outbox/drafts');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken),
  CURLOPT_POSTFIELDS => array('files' => new CURLFile('./contract.pdf', 'application/pdf', 'contract.pdf'))
));
$drafts = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var content = new MultipartFormDataContent();
content.Add(new StreamContent(File.OpenRead("contract.pdf")), "files", "contract.pdf");
using var request = new HttpRequestMessage(HttpMethod.Post, "https://pre-aws.postamail.es/api/outbox/drafts") { Content = content };
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
var drafts = await response.Content.ReadFromJsonAsync<DraftInfo[]>();
===java
var body = MultipartBodyPublisher.newBuilder()
  .filePart("files", Path.of("contract.pdf"), "application/pdf")
  .build();
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/outbox/drafts"))
  .header("Authorization", "Bearer " + jwtToken)
  .POST(body)
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## 3. Confirm the shipment {#3-confirm-the-shipment}

\`\`\`endpoint
PUT /api/outbox/shipments
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/outbox/shipments\` |
| Operation | \`PUT\` |
| Header | \`Authorization: Bearer <jwtToken>\`; \`Content-Type: application/json\` |
| Body | [Shipment](/docs/api/shipment-objects#shipment) |

Confirm the shipment with a [Shipment](/docs/api/shipment-objects#shipment) body. Set \`emissionTime\` to the current/past UTC time for immediate emission, or to a future UTC time for scheduled emission.

\`countryIso\` must always follow ISO alpha-2. For the sender address, \`identityCard\` is mandatory and must comply with the applicable NIF/DNI rules.

\`\`\`json
{
  "emissionTime": "2026-07-01T10:30:00Z",
  "cancelTime": null,
  "pages": [
    { "draftId": "<draftId from upload>", "pageFirst": 1, "pageLast": 5 }
  ],
  "destinationAddress": {
    "who": "Juan Perez",
    "contact": "Departamento Legal",
    "phone": "+34910000000",
    "identityCard": "00000000T",
    "countryIso": "ES",
    "locality": "Leganes",
    "postalCode": "28914",
    "streetType": "Calle",
    "streetName": "Mayor",
    "streetNumber": "1",
    "streetComplements": "2 A",
    "envelopeBox": null
  },
  "senderAddress": {
    "who": "ACME",
    "identityCard": "B00000000",
    "countryIso": "ES",
    "locality": "Madrid",
    "postalCode": "28002",
    "streetType": "Calle",
    "streetName": "Origen",
    "streetNumber": "10"
  },
  "features": {
    "productCode": "BCC",
    "printColor": false,
    "printDuplex": false,
    "reference": "ACME-2026-0001",
    "price": 726,
    "priceFirstSheet": 120,
    "priceNextSheet": 35,
    "deliveryPrice": 480,
    "vat": 21
  }
}
\`\`\`

The response is the same [Shipment](/docs/api/shipment-objects#shipment) object with \`shipmentId\` and \`shipmentCode\` assigned. Keep both identifiers; \`shipmentCode\` is the human-friendly code used in later lookups.

\`\`\`tabs-shipment-confirm
===curl
curl -X PUT "https://pre-aws.postamail.es/api/outbox/shipments" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -H "Content-Type: application/json" \\
  -d @shipment.json
===node
const response = await fetch('https://pre-aws.postamail.es/api/outbox/shipments', {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + jwtToken,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(shipment)
})
const confirmedShipment = await response.json()
===python
response = requests.put(
  'https://pre-aws.postamail.es/api/outbox/shipments',
  headers={'Authorization': 'Bearer ' + jwt_token},
  json=shipment
)
response.raise_for_status()
confirmed_shipment = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$shipment = array(); // Fill with the Shipment body shown above.
$curl = curl_init('https://pre-aws.postamail.es/api/outbox/shipments');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => 'PUT',
  CURLOPT_HTTPHEADER => array(
    'Authorization: Bearer ' . $jwtToken,
    'Content-Type: application/json'
  ),
  CURLOPT_POSTFIELDS => json_encode($shipment)
));
$confirmedShipment = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Put, "https://pre-aws.postamail.es/api/outbox/shipments")
{
  Content = JsonContent.Create(shipment)
};
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
var confirmedShipment = await response.Content.ReadFromJsonAsync<Shipment>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/outbox/shipments"))
  .header("Authorization", "Bearer " + jwtToken)
  .header("Content-Type", "application/json")
  .PUT(HttpRequest.BodyPublishers.ofString(shipmentJson))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## Cancel a non-processed shipment

A shipment can be cancelled while it has not yet been processed. Cancellation is performed with the same endpoint used to create/update shipments: send the complete \`Shipment\` object again, preserving its metadata and setting \`cancelTime\` to the current UTC time.

> Cancellation is an update operation. Do not send only \`shipmentId\`, \`shipmentCode\` and \`cancelTime\` as a partial object; fetch or keep the complete \`Shipment\`, update \`cancelTime\`, and send that complete object back.

### Step 1: get the shipment metadata {#step-1-get-the-shipment-metadata}

If you do not already have the shipment metadata, fetch it first:

\`\`\`endpoint
GET /api/outbox/shipments/{shipmentCode}
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/outbox/shipments/{shipmentCode}\` |
| Operation | \`GET\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`NULL\` |

\`\`\`tabs-shipment-get
===curl
curl -X GET "https://pre-aws.postamail.es/api/outbox/shipments/PR5UKZ4NM" \\
  -H "Authorization: Bearer <jwtToken>"
===node
const response = await fetch('https://pre-aws.postamail.es/api/outbox/shipments/PR5UKZ4NM', {
  headers: { Authorization: 'Bearer ' + jwtToken }
})
const shipment = await response.json()
===python
response = requests.get(
  'https://pre-aws.postamail.es/api/outbox/shipments/PR5UKZ4NM',
  headers={'Authorization': 'Bearer ' + jwt_token}
)
shipment = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$curl = curl_init('https://pre-aws.postamail.es/api/outbox/shipments/PR5UKZ4NM');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)
));
$shipment = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Get, "https://pre-aws.postamail.es/api/outbox/shipments/PR5UKZ4NM");
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var shipment = await response.Content.ReadFromJsonAsync<Shipment>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/outbox/shipments/PR5UKZ4NM"))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

The result of this call is the \`Shipment\` object that must be used as the cancellation body.

### Step 2: set cancellation fields

Update the retrieved \`Shipment\` object with the cancellation data:

| Field | Value |
|-------|-------|
| \`shipmentId\` | Keep the identifier returned by PostaMail, e.g. \`074523a4-2e3d-4815-96c4-b8e2b5bb0b88\`. |
| \`shipmentCode\` | Keep the human-readable shipment code, e.g. \`PR5UKZ4NM\`. |
| \`cancelTime\` | Set to the current UTC date/time. Any valid current cancellation date is accepted while the shipment is cancellable. |

All other fields in the \`Shipment\` object must remain unchanged.

### Step 3: submit the cancellation update {#step-3-submit-the-cancellation-update}

\`\`\`endpoint
PUT /api/outbox/shipments?sendconfirmation=false
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false\` |
| Operation | \`PUT\` |
| Header | \`Authorization: Bearer <jwtToken>\`; \`Content-Type: application/json\` |
| Body | Complete [Shipment](/docs/api/shipment-objects#shipment) object with \`cancelTime\` set |

Minimal excerpt of the fields that change or identify the shipment:

\`\`\`json
{
  "shipmentId": "074523a4-2e3d-4815-96c4-b8e2b5bb0b88",
  "shipmentCode": "PR5UKZ4NM",
  "cancelTime": "2026-07-01T11:00:00Z"
}
\`\`\`

The actual request body must be the complete \`Shipment\` object returned by the previous lookup with \`cancelTime\` changed.

\`\`\`tabs-shipment-cancel
===curl
curl -X PUT "https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -H "Content-Type: application/json" \\
  -d @shipment-cancelled.json
===node
shipment.cancelTime = new Date().toISOString()
const response = await fetch('https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false', {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + jwtToken,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(shipment)
})
const cancelledShipment = await response.json()
===python
from datetime import datetime, timezone

shipment['cancelTime'] = datetime.now(timezone.utc).isoformat()
response = requests.put(
  'https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false',
  headers={'Authorization': 'Bearer ' + jwt_token},
  json=shipment
)
cancelled_shipment = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$shipment['cancelTime'] = gmdate('Y-m-d\\TH:i:s\\Z');
$curl = curl_init('https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => 'PUT',
  CURLOPT_HTTPHEADER => array(
    'Authorization: Bearer ' . $jwtToken,
    'Content-Type: application/json'
  ),
  CURLOPT_POSTFIELDS => json_encode($shipment)
));
$cancelledShipment = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
shipment.cancelTime = DateTime.UtcNow;
using var request = new HttpRequestMessage(HttpMethod.Put, "https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false")
{
  Content = JsonContent.Create(shipment)
};
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var cancelledShipment = await response.Content.ReadFromJsonAsync<Shipment>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/outbox/shipments?sendconfirmation=false"))
  .header("Authorization", "Bearer " + jwtToken)
  .header("Content-Type", "application/json")
  .PUT(HttpRequest.BodyPublishers.ofString(cancelledShipmentJson))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

### Cancellation response

The response is a \`Shipment\` object:

| Result | Meaning |
|--------|---------|
| \`cancelTime\` is not \`null\` | Cancellation was accepted. |
| \`cancelTime\` is \`null\` | Cancellation could not be accepted, normally because the shipment had already been processed or was no longer cancellable. |

Because cancellation is implemented as a shipment update, the same \`PUT /api/outbox/shipments\` method can also be used to edit shipment metadata while the shipment has not yet been processed.

## List shipments {#list-shipments}

\`\`\`endpoint
POST /api/outbox/shipmentlist
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/outbox/shipmentlist\` |
| Operation | \`POST\` |
| Header | \`Authorization: Bearer <jwtToken>\`; \`Content-Type: application/json\` |
| Body | \`shipmentCodes\`: non-empty JSON array of shipment code strings |

Use this endpoint to retrieve shipments by code. Send \`shipmentCodes\` as a non-empty JSON array in the request body, not as a query parameter or an object containing a \`shipmentCodes\` property. The response is an array of [Shipment](/docs/api/shipment-objects#shipment) objects.

> **Maximum 100 shipments per request**
> Arrays with more than 100 shipment codes are rejected with HTTP \`400 Bad Request\` and \`ERR_OUTBOX_TOO_MANY_SHIPMENTS\`. Split larger arrays into groups of up to 100 codes and send a separate request for each group.

\`\`\`json
["PR5UKZ4NM", "PXXDMYXXM"]
\`\`\`

\`\`\`tabs-shipment-list
===curl
curl -X POST "https://pre-aws.postamail.es/api/outbox/shipmentlist" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -H "Content-Type: application/json" \\
  -d '["PR5UKZ4NM","PXXDMYXXM"]'
===node
const shipmentCodes = ['PR5UKZ4NM', 'PXXDMYXXM']
const response = await fetch('https://pre-aws.postamail.es/api/outbox/shipmentlist', {
  method: 'POST',
  headers: { Authorization: 'Bearer ' + jwtToken, 'Content-Type': 'application/json' },
  body: JSON.stringify(shipmentCodes)
})
const shipments = await response.json()
===python
shipment_codes = ['PR5UKZ4NM', 'PXXDMYXXM']
response = requests.post(
  'https://pre-aws.postamail.es/api/outbox/shipmentlist',
  headers={'Authorization': 'Bearer ' + jwt_token},
  json=shipment_codes
)
shipments = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$shipmentCodes = array('PR5UKZ4NM', 'PXXDMYXXM');
$curl = curl_init('https://pre-aws.postamail.es/api/outbox/shipmentlist');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken, 'Content-Type: application/json'),
  CURLOPT_POSTFIELDS => json_encode($shipmentCodes)
));
$shipments = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
var shipmentCodes = new[] { "PR5UKZ4NM", "PXXDMYXXM" };
using var request = new HttpRequestMessage(HttpMethod.Post, "https://pre-aws.postamail.es/api/outbox/shipmentlist")
{
  Content = JsonContent.Create(shipmentCodes)
};
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var shipments = await response.Content.ReadFromJsonAsync<Shipment[]>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/outbox/shipmentlist"))
  .header("Authorization", "Bearer " + jwtToken)
  .header("Content-Type", "application/json")
  .POST(HttpRequest.BodyPublishers.ofString("[\\"PR5UKZ4NM\\",\\"PXXDMYXXM\\"]"))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`
`



// WEBPACK FOOTER //
// ./src/components/docs/api/content/shipments.js